import { useState } from "react";
import { useInvoices, useUpdateInvoiceStatus, STATUS_INVOICE_LABEL, STATUS_INVOICE_PILL, type InvoiceStatus } from "@/lib/invoices";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

const ALL_STATUS: InvoiceStatus[] = ["open", "partly_paid", "paid", "overdue", "cancelled"];

export default function RechnungenPage() {
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | undefined>("open");
  const { data: invoices = [], isLoading } = useInvoices({ status: statusFilter });
  const upd = useUpdateInvoiceStatus();

  const totalOpen = invoices
    .filter((i) => i.status === "open" || i.status === "overdue" || i.status === "partly_paid")
    .reduce((a, i) => a + (i.total_cents - i.paid_cents), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Rechnungen</h1>
        <div className="text-right">
          <div className="text-sm text-hof-500">Offene Posten</div>
          <div className="text-2xl font-bold text-sonne-700">{fmt.format(totalOpen / 100)}</div>
        </div>
      </div>

      <div className="card flex flex-wrap gap-2 items-center">
        <span className="text-sm text-hof-700">Status:</span>
        <button onClick={() => setStatusFilter(undefined)}
          className={`px-3 py-1.5 rounded-full text-sm border ${!statusFilter ? "border-wiese-500 bg-wiese-50 text-wiese-800" : "border-hof-200 text-hof-600 hover:bg-hof-50"}`}>
          Alle
        </button>
        {ALL_STATUS.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm border ${statusFilter === s ? "border-wiese-500 bg-wiese-50 text-wiese-800" : "border-hof-200 text-hof-600 hover:bg-hof-50"}`}>
            {STATUS_INVOICE_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? <div className="card">Lade…</div>
        : invoices.length === 0 ? <div className="card text-center text-hof-600">Keine Rechnungen mit diesem Filter.</div>
        : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-hof-50 text-sm text-hof-600">
              <tr>
                <th className="p-3">Nr.</th>
                <th className="p-3">Kunde</th>
                <th className="p-3">Datum</th>
                <th className="p-3">Fällig</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Summe</th>
                <th className="p-3 text-right">Offen</th>
                <th className="p-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const offen = inv.total_cents - inv.paid_cents;
                const overdue = inv.status === "open" && new Date(inv.due_at) < new Date();
                return (
                  <tr key={inv.id} className={`border-t border-hof-100 hover:bg-hof-50/50 ${overdue ? "bg-red-50/30" : ""}`}>
                    <td className="p-3 font-mono text-sm">{inv.invoice_number}</td>
                    <td className="p-3">{inv.customer?.display_name ?? "—"} {inv.customer?.type === "gewerblich" && <span className="pill-info text-xs ml-1">Gewerblich</span>}</td>
                    <td className="p-3 text-sm">{dfmt.format(new Date(inv.created_at))}</td>
                    <td className="p-3 text-sm">{dfmt.format(new Date(inv.due_at))}</td>
                    <td className="p-3"><span className={STATUS_INVOICE_PILL[overdue ? "overdue" : inv.status]}>{STATUS_INVOICE_LABEL[overdue ? "overdue" : inv.status]}</span></td>
                    <td className="p-3 text-right font-mono">{fmt.format(inv.total_cents / 100)}</td>
                    <td className="p-3 text-right font-mono">{offen > 0 ? fmt.format(offen / 100) : "—"}</td>
                    <td className="p-3">
                      {inv.status !== "paid" && inv.status !== "cancelled" && (
                        <button onClick={() => upd.mutate({ id: inv.id, status: "paid", paidCents: inv.total_cents })}
                          className="btn-secondary text-xs">✓ Als bezahlt</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
